import { useEffect, useState, useSyncExternalStore } from "react";
import { useT, type Translate } from "../../i18n/hooks.ts";
import {
  fetchStationInventory,
  fixedBound,
  nearestStations,
  proposedFreeBound,
  type AemetStation,
  type DateRange,
  type ExtendDirection,
  type StationSelectionMethod,
} from "../../model/aemet/aemet.ts";
import { aemetJobStore } from "../../model/aemet/aemetJobStore.ts";
import {
  clampToRocioCoverage,
  defaultRangeEnd,
  defaultRangeStart,
  deriveRangeInfo,
  extendBatchStatus,
} from "../../model/aemet/aemetRangeMath.ts";
import type { ProjectSeries } from "../../model/dailySeries.ts";
import { subBasinCentroid, subBasinGeometry, type SubBasinLayer } from "../../model/gisLayer.ts";
import { daysBetweenIso } from "../../model/isoDate.ts";
import { ROCIO_COVERAGE_END, ROCIO_COVERAGE_START } from "../../model/rocio.ts";
import type { AemetProvenance } from "../../model/storage.ts";
import type { EvalHidProject, SubBasin } from "../../model/types.ts";
import { missingSeriesFor } from "../../model/subBasinState.ts";
import { AemetAttributionNotice } from "./AemetAttributionNotice.tsx";
import { AemetDownloadProgress } from "./AemetDownloadProgress.tsx";
import { mostCommonMethod, scopeSubBasins } from "./aemetBulkMethod.ts";
import { Dialog } from "../Dialog.tsx";
import { InfoIcon } from "../InfoIcon.tsx";
import { InlineProgress } from "../InlineProgress.tsx";
import { Pagination } from "../Pagination.tsx";
import { useAemetFetch } from "./useAemetFetch.ts";

// Real usability problem reported live: with well over a thousand pending
// sub-basins (a real MITECO import, the Duero: 1728), the row list's own
// scrollbar thumb became a sliver of the track, nearly impossible to drag
// precisely — paginating instead keeps browsing equally easy regardless of
// how many rows there are. 20 is small enough to fit comfortably in the
// dialog without its own scrolling, most of the time.
const PAGE_SIZE = 20;

interface Row {
  subBasin: SubBasin;
  subtitle: string;
  centroid: { lat: number; lon: number } | null;
  /** Only used by the "rocio" method (point-in-polygon cell selection) — see aemetJobStore.ts's `resolveCandidates`. */
  geometry: GeoJSON.Geometry | null;
  /** `null` while the (cached, shared) station inventory is still loading — see AemetBulkFetchDialog's own effect. Treated as "assume yes" until resolved. Station-distance only — see `hasCoverage` below for why this alone isn't the right gate once ROCIO is the method. */
  hasNearbyStation: boolean | null;
}

/**
 * Whether `row` can actually be fetched with `method` — real bug found live
 * on the Duero project (1728 sub-basins spanning a huge, partly mountainous
 * area): `row.hasNearbyStation` only ever checks distance to a physical
 * AEMET STATION (`nearestStations`, max 100km), which both "nearest" and
 * "idw" genuinely need — but ROCIO is a ~5km interpolated grid with
 * effectively universal coverage of Spain regardless of station distance
 * (aemetJobStore.ts's own `resolveCandidates` always finds at least the
 * nearest grid cell for ROCIO, no cutoff at all). 31 sub-basins far enough
 * from any station to fail the station check got silently excluded from
 * "extend all"/"download all" even with "Malla ROCIO" selected — no button,
 * just "Sin cobertura AEMET", with no way to retry them from this dialog.
 */
function hasCoverage(row: Row, method: StationSelectionMethod): boolean {
  return method === "rocio" || row.hasNearbyStation !== false;
}

/**
 * One sub-basin's row — a thin view over its own `aemetJobStore` job (same
 * hook AemetFetchDialog.tsx/EditSubBasinDialog.tsx use), so progress
 * updates without the whole list re-rendering (`useSyncExternalStore`
 * bails out for rows whose own job object reference didn't change — see
 * aemetJobStore.ts's `patchJob`). Deliberately shows no station names/
 * candidate list — that detail lives in the sub-basin's own "View AEMET
 * report" (AemetProvenanceDialog.tsx). Shared by both "complete missing"
 * and "extend range" modes — `actionLabel`/`subtitle`/`onAction` are what
 * differ between them.
 *
 * `isExtend` guards against a real trap: the SAME sub-basin can carry a job
 * left over from the OTHER mode (e.g. just finished "complete", now
 * viewing "extend" for the very first time) — a naive `aemet.phase` read
 * would show that stale job's own "report"/"downloading" instead of a
 * fresh action button, since aemetJobStore only tracks one job per
 * sub-basin. `effectivePhase` falls back to "idle" whenever the job's own
 * `extendRange` (present only for extend-mode jobs) doesn't match which
 * mode THIS row is being rendered for.
 */
function AemetBulkRow({
  row,
  series,
  isExtend,
  actionLabel,
  onAction,
  method,
}: {
  row: Row;
  series: ProjectSeries;
  isExtend: boolean;
  actionLabel: string;
  onAction: (row: Row) => void;
  method: StationSelectionMethod;
}) {
  const t = useT();
  const aemet = useAemetFetch({
    subBasinCode: row.subBasin.subBasinCode,
    subBasinName: row.subBasin.description,
    series,
    centroid: row.centroid,
    geometry: row.geometry,
  });
  const jobMatchesThisMode = isExtend ? aemet.extendRange !== null : aemet.extendRange === null;
  const effectivePhase = jobMatchesThisMode ? aemet.phase : "idle";

  return (
    <li className="aemet-bulk-row">
      <div>
        <strong>{row.subBasin.description}</strong>
        <p className="muted-text">{row.subtitle}</p>
      </div>
      <div>
        {effectivePhase === "idle" &&
          (row.centroid && !hasCoverage(row, method) ? (
            <p className="muted-text">{t("aemetBulkDialog.noCoverage")}</p>
          ) : (
            <button
              className="button"
              disabled={!row.centroid}
              title={row.centroid ? undefined : t("editSubBasinDialog.aemetNoGeometry")}
              onClick={() => onAction(row)}
            >
              {actionLabel}
            </button>
          ))}
        {effectivePhase === "loading-stations" && (
          <InlineProgress
            text={t(
              aemet.method === "rocio"
                ? "aemetDialog.searchingCells"
                : "aemetDialog.searchingStations"
            )}
          />
        )}
        {effectivePhase === "no-stations" && (
          <p className="error">
            {t(aemet.method === "rocio" ? "aemetDialog.noRocioCoverage" : "aemetDialog.noStations")}
          </p>
        )}
        {(effectivePhase === "stations-error" || effectivePhase === "download-error") && (
          <>
            <p className="error">{aemet.errorMessage}</p>
            <button className="button" onClick={() => onAction(row)}>
              {t("aemetBulkDialog.retry")}
            </button>
          </>
        )}
        {(effectivePhase === "range" || effectivePhase === "downloading") && (
          <AemetDownloadProgress
            done={aemet.downloadDone}
            total={aemet.downloadTotal}
            range={aemet.downloadRange}
          />
        )}
        {effectivePhase === "report" &&
          (aemet.applyErrorMessage ? (
            <>
              <p className="error">{aemet.applyErrorMessage}</p>
              <button
                className="button"
                onClick={() => aemetJobStore.retryApply(row.subBasin.subBasinCode)}
              >
                {t("aemetDialog.retryApply")}
              </button>
            </>
          ) : (aemet.downloadResult?.failedWindows.length ?? 0) > 0 ? (
            <p className="muted-text">
              {t("aemetBulkDialog.completedWithGaps", {
                n: String(aemet.downloadResult?.failedWindows.length ?? 0),
              })}
            </p>
          ) : (
            <p>{t("aemetBulkDialog.completed")}</p>
          ))}
      </div>
    </li>
  );
}

function missingLabel(t: Translate, subBasin: SubBasin, series: ProjectSeries): string {
  const missing = missingSeriesFor(series, subBasin);
  return [
    missing.precipitation && t("infoPopover.precipitation"),
    missing.etp && t("infoPopover.etp"),
    missing.temperature && t("infoPopover.temperature"),
  ]
    .filter((v): v is string => !!v)
    .join(", ");
}

/** Shared by both modes — same "nearest"/"idw"/"rocio" choice AemetFetchDialog.tsx offers for a single sub-basin, here applying to the whole batch at once. */
function MethodSelector({
  method,
  onChange,
}: {
  method: StationSelectionMethod;
  onChange: (method: StationSelectionMethod) => void;
}) {
  const t = useT();
  return (
    <>
      <div className="assigner-controls">
        <label>{t("aemetDialog.methodTitle")}</label>
        <select value={method} onChange={(e) => onChange(e.target.value as StationSelectionMethod)}>
          <option value="nearest">{t("aemetDialog.methodNearest")}</option>
          <option value="idw">{t("aemetDialog.methodIdw")}</option>
          <option value="rocio">{t("aemetDialog.methodRocio")}</option>
        </select>
      </div>
      <p className="muted-text">
        {t(
          method === "idw"
            ? "aemetDialog.methodIdwHelp"
            : method === "rocio"
              ? "aemetDialog.methodRocioHelp"
              : "aemetDialog.methodNearestHelp"
        )}
      </p>
    </>
  );
}

/** "Complete missing" mode's body — the original (and only) behavior this dialog had before "extend range" existed. */
function AemetCompleteBody({
  rows,
  series,
  forcedRange,
  defaultMethod,
}: {
  rows: Row[];
  series: ProjectSeries;
  forcedRange: DateRange | null;
  defaultMethod: StationSelectionMethod;
}) {
  const t = useT();
  // If the project's already using ROCIO for most sub-basins, defaultMethod
  // starts this dialog on "rocio" too (see mostCommonMethod's own comment)
  // — the free-range default has to be clamped into ROCIO's real coverage
  // from the START in that case, same as handleMethodChange already does
  // when switching TO "rocio" mid-dialog, or the date inputs would open
  // showing a range ROCIO can't actually serve (the generic default reaches
  // through yesterday, well past ROCIO's 2022 cutoff).
  const initialFreeRange =
    defaultMethod === "rocio"
      ? clampToRocioCoverage({ start: defaultRangeStart(), end: defaultRangeEnd() })
      : { start: defaultRangeStart(), end: defaultRangeEnd() };
  const [freeStart, setFreeStart] = useState(initialFreeRange.start);
  const [freeEnd, setFreeEnd] = useState(initialFreeRange.end);
  const [method, setMethod] = useState<StationSelectionMethod>(defaultMethod);
  // Tracks a startQueued() batch triggered from the button below — real bug
  // reported live: neither the summary text nor the button itself gave any
  // sign a batch was actually running (only the "(N)" count quietly ticking
  // down), so the button kept looking clickable/idle the whole time,
  // confusing enough that it wasn't obvious anything had started. `total`
  // is captured once, at the moment the batch starts, so the progress
  // count below doesn't reset as `rows` itself shrinks.
  const [batch, setBatch] = useState<{ targetCodes: Set<number>; total: number } | null>(null);
  const [page, setPage] = useState(0);
  const validRange = forcedRange !== null || freeStart <= freeEnd;

  // Switching TO "rocio" clamps the free range into its real 1951-2022
  // coverage — see clampToRocioCoverage's own comment (aemetJobStore.ts).
  function handleMethodChange(newMethod: StationSelectionMethod) {
    setMethod(newMethod);
    if (newMethod !== "rocio") return;
    const clamped = clampToRocioCoverage({ start: freeStart, end: freeEnd });
    setFreeStart(clamped.start);
    setFreeEnd(clamped.end);
  }

  if (rows.length === 0) {
    // Every sub-basin this mode was tracking finished downloading while the
    // dialog stayed open (rows are computed fresh from live props) — mode
    // stays "complete" (sticky, see AemetBulkFetchDialog's own `chosenMode`)
    // instead of silently swapping to "extend" underneath the user.
    return <p>{t("aemetBulkDialog.allComplete")}</p>;
  }

  function download(row: Row) {
    if (!row.centroid || !validRange) return;
    aemetJobStore.start(row.subBasin.subBasinCode, row.subBasin.description, row.centroid, {
      freeStart: forcedRange ? undefined : freeStart,
      freeEnd: forcedRange ? undefined : freeEnd,
      method,
      geometry: row.geometry,
      autoDownload: true,
    });
  }

  const downloadableRows = rows.filter((r) => r.centroid !== null && hasCoverage(r, method));

  async function downloadAll() {
    const targets = downloadableRows;
    setBatch({
      targetCodes: new Set(targets.map((r) => r.subBasin.subBasinCode)),
      total: targets.length,
    });
    try {
      await aemetJobStore.startQueuedWithRetry(
        targets.map((row) => ({
          subBasinCode: row.subBasin.subBasinCode,
          subBasinName: row.subBasin.description,
          centroid: row.centroid!,
          opts: {
            freeStart: forcedRange ? undefined : freeStart,
            freeEnd: forcedRange ? undefined : freeEnd,
            method,
            geometry: row.geometry,
          },
        }))
      );
    } finally {
      setBatch(null);
    }
  }

  // How many of THIS batch's own targets are no longer in `rows` (i.e. no
  // longer missing) — not just `batch.total - rows.length`, since `rows`
  // can also include sub-basins this batch never targeted (no geometry, no
  // nearby coverage) that will never leave it on their own.
  const batchDone = batch
    ? batch.total - rows.filter((r) => batch.targetCodes.has(r.subBasin.subBasinCode)).length
    : 0;

  const pageCount = Math.ceil(rows.length / PAGE_SIZE);
  const currentPage = pageCount === 0 ? 0 : Math.min(page, pageCount - 1);
  const pageRows = rows.slice(currentPage * PAGE_SIZE, currentPage * PAGE_SIZE + PAGE_SIZE);

  return (
    <>
      <p>
        {t(rows.length === 1 ? "aemetBulkDialog.oneMissing" : "aemetBulkDialog.multipleMissing", {
          n: String(rows.length),
        })}
      </p>

      {forcedRange ? (
        <p className="muted-text">
          {t("aemetDialog.rangeFixedNotice", { from: forcedRange.start, to: forcedRange.end })}
        </p>
      ) : (
        <>
          <h4>
            {t("aemetDialog.rangeTitle")}{" "}
            {method === "rocio" && <InfoIcon text={t("aemetDialog.rocioRangeLimit")} />}
          </h4>
          <p className="muted-text">{t("aemetBulkDialog.rangeHelp")}</p>
          <div className="assigner-controls">
            <label>{t("aemetDialog.startDate")}</label>
            <input
              type="date"
              value={freeStart}
              min={method === "rocio" ? ROCIO_COVERAGE_START : undefined}
              max={method === "rocio" ? ROCIO_COVERAGE_END : undefined}
              onChange={(e) => setFreeStart(e.target.value)}
            />
          </div>
          <div className="assigner-controls">
            <label>{t("aemetDialog.endDate")}</label>
            <input
              type="date"
              value={freeEnd}
              min={method === "rocio" ? ROCIO_COVERAGE_START : undefined}
              max={method === "rocio" ? ROCIO_COVERAGE_END : undefined}
              onChange={(e) => setFreeEnd(e.target.value)}
            />
          </div>
          {!validRange && <p className="error">{t("aemetDialog.invalidRange")}</p>}
        </>
      )}

      <MethodSelector method={method} onChange={handleMethodChange} />

      <div className="dialog-actions">
        <button
          className="button button-primary"
          disabled={downloadableRows.length === 0 || !validRange || batch !== null}
          onClick={() => void downloadAll()}
        >
          {batch
            ? t("aemetBulkDialog.downloadingAll", {
                done: String(batchDone),
                total: String(batch.total),
              })
            : t("aemetBulkDialog.downloadAll", { n: String(downloadableRows.length) })}
        </button>
      </div>

      <Pagination page={currentPage} pageCount={pageCount} onPageChange={setPage} />
      <ul className="editable-list">
        {pageRows.map((row) => (
          <AemetBulkRow
            key={row.subBasin.subBasinCode}
            row={row}
            series={series}
            isExtend={false}
            actionLabel={t("aemetBulkDialog.download")}
            onAction={download}
            method={method}
          />
        ))}
      </ul>
    </>
  );
}

/**
 * "Extend range" mode's body — fetches a NEW window for every sub-basin
 * that already has AEMET-sourced data (has an `aemetProvenance` entry),
 * merging into the project's existing series instead of requiring an exact
 * date match (`extendSeriesRange`, dailySeries.ts). The new window is always
 * CONTIGUOUS with the existing one (fixed to start exactly the day after it
 * ends, or end exactly the day before it starts) — only one date is ever
 * user-editable, by construction, so there's no way to leave a real
 * calendar gap between old and new dates (which would break the "daily,
 * no gaps" assumption the rest of the app relies on for a series' own date
 * axis, unlike the gaps THIS feature deliberately allows in OTHER
 * sub-basins' columns during the new window — see extendSeriesRange's own
 * comment for that distinction).
 */
function AemetExtendBody({
  rows,
  series,
  forcedRange,
  defaultMethod,
}: {
  rows: Row[];
  series: ProjectSeries;
  forcedRange: DateRange;
  defaultMethod: StationSelectionMethod;
}) {
  const t = useT();
  const [direction, setDirection] = useState<ExtendDirection>(() =>
    daysBetweenIso(forcedRange.end, defaultRangeEnd()) > 0 ? "forward" : "back"
  );
  const [freeBound, setFreeBound] = useState(() =>
    proposedFreeBound(forcedRange, direction, defaultRangeEnd())
  );
  const [method, setMethod] = useState<StationSelectionMethod>(defaultMethod);
  const [page, setPage] = useState(0);

  useEffect(() => {
    setFreeBound(proposedFreeBound(forcedRange, direction, defaultRangeEnd()));
    // Only re-propose a default when the user switches direction — not on
    // every forcedRange identity change, which would overwrite whatever
    // they'd already typed the moment anything else in the project changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [direction]);

  // Real bug reported live: `forcedRange` (and everything derived from it —
  // `fixed`, the default `freeBound`, `validRange`) is computed straight from
  // the project's LIVE series (deriveRangeInfo, called on every render with
  // whatever `series.precipitation.dates` currently is). Once "Extend all"
  // starts, results stream back in and get merged sub-basin by sub-basin —
  // the shared date axis grows the moment the FIRST one finishes, long
  // before the other ~1700 do. That shifted `forcedRange` on every
  // subsequent render, which shifted `fixed` under the user's still-unchanged
  // `freeBound`, until the two crossed and "the end date must be after the
  // start" started showing — while a real download kept running underneath,
  // unaffected. Worse, none of this survived closing and reopening the
  // dialog either, since it all lived in this component's own `useState`.
  //
  // Fix (see extendBatchStatus's own comment for the full reasoning): while
  // a batch is actually in flight, read its already-recorded `extendRange`
  // from aemetJobStore instead of recomputing anything live.
  const jobs = useSyncExternalStore(aemetJobStore.subscribe, aemetJobStore.getAllJobs);
  const rowCodes = new Set(rows.map((r) => r.subBasin.subBasinCode));
  const {
    total: batchTotal,
    done: batchDone,
    running: batchRunning,
    range: frozenRange,
  } = extendBatchStatus(jobs, rowCodes);

  const fixed = fixedBound(forcedRange, direction);
  const liveExtendRange: DateRange =
    direction === "forward" ? { start: fixed, end: freeBound } : { start: freeBound, end: fixed };
  const extendRange = frozenRange ?? liveExtendRange;
  const validRange = extendRange.start <= extendRange.end;

  function extend(row: Row) {
    if (!row.centroid || !validRange || batchRunning) return;
    aemetJobStore.start(row.subBasin.subBasinCode, row.subBasin.description, row.centroid, {
      extendRange,
      method,
      geometry: row.geometry,
      autoDownload: true,
    });
  }

  const extendableRows = rows.filter((r) => r.centroid !== null && hasCoverage(r, method));

  async function extendAll() {
    // startQueuedExtendWithRetry, not plain startQueued — a large batch
    // (Duero: 1728 sub-basins) hits the same "small residual left over"
    // pattern the initial download's own startQueuedWithRetry already
    // guards against; extend needs its own version since "still needs
    // this" means something different here (a gap inside `extendRange`,
    // not "no column at all" — see that function's own comment).
    await aemetJobStore.startQueuedExtendWithRetry(
      extendableRows.map((row) => ({
        subBasinCode: row.subBasin.subBasinCode,
        subBasinName: row.subBasin.description,
        centroid: row.centroid!,
        opts: { extendRange, method, geometry: row.geometry },
      })),
      extendRange
    );
  }

  const pageCount = Math.ceil(rows.length / PAGE_SIZE);
  const currentPage = pageCount === 0 ? 0 : Math.min(page, pageCount - 1);
  const pageRows = rows.slice(currentPage * PAGE_SIZE, currentPage * PAGE_SIZE + PAGE_SIZE);

  return (
    <>
      <p className="muted-text">
        {t("aemetBulkDialog.currentRange", { from: forcedRange.start, to: forcedRange.end })}
      </p>
      <h4>{t("aemetBulkDialog.newRangeTitle")}</h4>
      {batchRunning && frozenRange ? (
        <>
          <p className="muted-text">
            {t("aemetBulkDialog.extendingAllRange", {
              from: frozenRange.start,
              to: frozenRange.end,
            })}
          </p>
          <div className="aemet-job-progress">
            <div className="aemet-job-progress-track">
              <div
                className="aemet-job-progress-fill"
                style={{ width: `${Math.round((batchDone / batchTotal) * 100)}%` }}
              />
            </div>
          </div>
        </>
      ) : (
        <>
          <p className="muted-text">{t("aemetBulkDialog.newRangeHelp")}</p>
          <div className="assigner-controls">
            <label>{t("aemetBulkDialog.direction")}</label>
            <select
              value={direction}
              onChange={(e) => setDirection(e.target.value as ExtendDirection)}
            >
              <option value="forward">{t("aemetBulkDialog.directionForward")}</option>
              <option value="back">{t("aemetBulkDialog.directionBackward")}</option>
            </select>
          </div>
          {direction === "forward" ? (
            <>
              <div className="assigner-controls">
                <label>{t("aemetDialog.startDate")}</label>
                <span>{fixed}</span>
              </div>
              <div className="assigner-controls">
                <label>{t("aemetDialog.endDate")}</label>
                <input
                  type="date"
                  value={freeBound}
                  onChange={(e) => setFreeBound(e.target.value)}
                />
              </div>
            </>
          ) : (
            <>
              <div className="assigner-controls">
                <label>{t("aemetDialog.startDate")}</label>
                <input
                  type="date"
                  value={freeBound}
                  onChange={(e) => setFreeBound(e.target.value)}
                />
              </div>
              <div className="assigner-controls">
                <label>{t("aemetDialog.endDate")}</label>
                <span>{fixed}</span>
              </div>
            </>
          )}
          {!validRange && <p className="error">{t("aemetDialog.invalidRange")}</p>}

          <MethodSelector method={method} onChange={setMethod} />
        </>
      )}

      <div className="dialog-actions">
        <button
          className="button button-primary"
          disabled={extendableRows.length === 0 || !validRange || batchRunning}
          onClick={() => void extendAll()}
        >
          {batchRunning
            ? t("aemetBulkDialog.extendingAll", {
                done: String(batchDone),
                total: String(batchTotal),
              })
            : t("aemetBulkDialog.extendAll", { n: String(extendableRows.length) })}
        </button>
      </div>

      <Pagination page={currentPage} pageCount={pageCount} onPageChange={setPage} />
      <ul className="editable-list">
        {pageRows.map((row) => (
          <AemetBulkRow
            key={row.subBasin.subBasinCode}
            row={row}
            series={series}
            isExtend={true}
            actionLabel={t("aemetBulkDialog.extend")}
            onAction={extend}
            method={method}
          />
        ))}
      </ul>
    </>
  );
}

type Mode = "complete" | "extend";

/**
 * Two AEMET operations for the whole project in one dialog, opened from
 * SeriesPanel.tsx's always-visible "Get from AEMET" button (closing the
 * editor panel first, same reasoning as EditSubBasinDialog.tsx's own AEMET
 * button): "complete missing" (any sub-basin without precipitation/ETP/
 * temperature yet — AemetCompleteBody, the original behavior) and "extend
 * range" (grow the date coverage of sub-basins that already have AEMET
 * data — AemetExtendBody, new). When BOTH apply, asks which one up front
 * instead of guessing; when only one does (or neither), skips straight to
 * it (or to a "nothing to do" message).
 */
export function AemetBulkFetchDialog({
  project,
  series,
  gisLayer,
  aemetProvenance,
  onlySubBasinCodes,
  onClose,
}: {
  project: EvalHidProject;
  series: ProjectSeries;
  gisLayer: SubBasinLayer | null;
  aemetProvenance: Record<number, AemetProvenance>;
  /** Restricts both lists below to just these sub-basins instead of the whole project — SeriesPanel.tsx's "gap left over from an extend" notice opens the dialog this way, so its "Extend all" only touches the affected rows. `null`/omitted keeps the usual whole-project behavior. */
  onlySubBasinCodes?: number[] | null;
  onClose: () => void;
}) {
  const t = useT();
  const { forcedRange } = deriveRangeInfo(undefined, series);
  const scopedSubBasins = scopeSubBasins(project.subBasins, onlySubBasinCodes);

  // Fetched once for the whole list (module-level cached in aemet.ts anyway,
  // but one effect here instead of AemetBulkRow doing its own per-row fetch)
  // — lets a sub-basin clearly outside AEMET's coverage show that instead of
  // a "Download" button that would only ever end in "no stations found".
  const [stations, setStations] = useState<AemetStation[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetchStationInventory().then((s) => {
      if (!cancelled) setStations(s);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  function rowFor(subBasin: SubBasin, subtitle: string): Row {
    const centroid = gisLayer ? subBasinCentroid(gisLayer, subBasin.subBasinCode) : null;
    const geometry = gisLayer ? subBasinGeometry(gisLayer, subBasin.subBasinCode) : null;
    return {
      subBasin,
      subtitle,
      centroid,
      geometry,
      hasNearbyStation:
        centroid && stations ? nearestStations(centroid, stations).length > 0 : null,
    };
  }

  const missingRows = scopedSubBasins
    .filter((sb) => {
      const missing = missingSeriesFor(series, sb);
      return missing.precipitation || missing.etp || missing.temperature;
    })
    .map((sb) => rowFor(sb, missingLabel(t, sb, series)));

  const extendRows = scopedSubBasins
    .filter((sb) => aemetProvenance[sb.subBasinCode])
    .map((sb) => {
      const provenance = aemetProvenance[sb.subBasinCode];
      return rowFor(
        sb,
        t("aemetBulkDialog.currentData", {
          from: provenance.dateRange.start,
          to: provenance.dateRange.end,
        })
      );
    });

  const canComplete = missingRows.length > 0;
  const canExtend = forcedRange !== null && extendRows.length > 0;
  const defaultMethod = mostCommonMethod(aemetProvenance) ?? "nearest";

  const [chosenMode, setChosenMode] = useState<Mode | null>(null);
  const bothAvailable = canComplete && canExtend;
  const mode: Mode | "none" | null =
    chosenMode ?? (bothAvailable ? null : canComplete ? "complete" : canExtend ? "extend" : "none");

  return (
    <Dialog title={t("aemetBulkDialog.title")} onClose={onClose}>
      <AemetAttributionNotice />

      {mode === null && (
        <>
          <p>{t("aemetBulkDialog.chooseMode")}</p>
          <div className="dialog-actions">
            <button className="button button-primary" onClick={() => setChosenMode("complete")}>
              {t("aemetBulkDialog.completeMode", { n: String(missingRows.length) })}
            </button>
            <button className="button" onClick={() => setChosenMode("extend")}>
              {t("aemetBulkDialog.extendMode")}
            </button>
          </div>
        </>
      )}

      {mode === "none" && <p>{t("aemetBulkDialog.nothingToDo")}</p>}

      {mode === "complete" && (
        <>
          {bothAvailable && (
            <button className="button-icon" onClick={() => setChosenMode(null)}>
              {t("aemetBulkDialog.back")}
            </button>
          )}
          <AemetCompleteBody
            rows={missingRows}
            series={series}
            forcedRange={forcedRange}
            defaultMethod={defaultMethod}
          />
        </>
      )}

      {mode === "extend" && forcedRange && (
        <>
          {bothAvailable && (
            <button className="button-icon" onClick={() => setChosenMode(null)}>
              {t("aemetBulkDialog.back")}
            </button>
          )}
          <AemetExtendBody
            rows={extendRows}
            series={series}
            forcedRange={forcedRange}
            defaultMethod={defaultMethod}
          />
        </>
      )}

      <div className="dialog-actions">
        <button className="button" onClick={onClose}>
          {t("common.close")}
        </button>
      </div>
    </Dialog>
  );
}
